/**
 * 提示条, 替代各页手写的红色错误框。
 * variant: danger (默认, role=alert) | warning | info | success (role=status)
 */
import type { ReactNode } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const alertVariants = cva("flex gap-2.5 rounded-lg border px-3 py-2.5 text-sm", {
  variants: {
    variant: {
      danger: "border-danger/25 bg-danger-soft text-danger-strong",
      warning: "border-warning/30 bg-warning-soft text-warning-strong",
      info: "border-info/25 bg-info-soft text-info-strong",
      success: "border-success/25 bg-success-soft text-success-strong",
    },
  },
  defaultVariants: { variant: "danger" },
});

const ICONS = { danger: AlertCircle, warning: AlertTriangle, info: Info, success: CheckCircle2 } as const;

export interface AlertProps extends VariantProps<typeof alertVariants> {
  title?: ReactNode;
  children?: ReactNode;
  /** 右侧操作 (如「去充值」按钮) */
  action?: ReactNode;
  /** 传了就显示关闭按钮 */
  onClose?: () => void;
  /** false 隐藏图标 */
  icon?: boolean;
  className?: string;
}

export function Alert({ variant, title, children, action, onClose, icon = true, className }: AlertProps) {
  const v = variant ?? "danger";
  const Icon = ICONS[v];
  return (
    <div role={v === "danger" ? "alert" : "status"} className={cn(alertVariants({ variant: v }), className)}>
      {icon && <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}
      <div className="min-w-0 flex-1 space-y-0.5">
        {title && <div className="font-medium">{title}</div>}
        {children && <div className={cn(title && "opacity-90")}>{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="-m-1 h-6 w-6 shrink-0 rounded-md p-1 opacity-70 hover:bg-black/5 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
